/// <reference path="global.d.ts" />
import { completions, generate, getWords, speak, transcribe, translate, writeError } from "./api";
import { syncReviewReminders } from "./push";
import { clearSyncSession, isSyncEnabled, pullSnapshot, pushSnapshot } from "./sync";
import { Language, LanguageCode, getLanguageName } from "./languages";
import tapbackAudio from './audio/tapback.mp3';
// import wowAudio from './audio/wow.mp3';
import sentAudio from './audio/sentmessage.mp3';
import loveAudio from './audio/love.mp3';
import playAudio from './audio/lets-play.mp3';
import rightAudio from './audio/correct.mp3';
import wrongAudio from './audio/wrong.mp3';
import finalAudio from './audio/final.mp3';
import finaleAudio from './audio/finale.mp3';
import { audio, wait } from "./audio/audio";
import sharedVersion from '../../shared/version.json';
import { migrateSnapshot, mergeSnapshots } from './migration';
import type { LocalizedValues } from "./localize";
import { diceCoefficient } from  'dice-coefficient';

type TypingState = 'idle' | 'typing';
const SNAPSHOT_VERSION = sharedVersion.version;

export type ConversationState = {
  revision: number;
  version: number;
  shock: boolean;
  expressions: Expression[];
  phraseRequests: PhraseRequest[];
  activityEvents: ActivityEvent[];
  messages: MessageEvent[];
  removedExpressions: number[];
  lastSyncAt?: string | null;
  lastSyncError?: string | null;
  pushEnabledDevices?: string[];
};

// function randomItem<T>(items: T[]): T { // min and max included 
//   return items[Math.floor(Math.random() * items.length)]
// }

// const WORD = /[\p{Letter}\p{Mark}]+/gu;

async function pronounce(
  expression: string,
  nativeLanguageCode: LanguageCode,
  options?: { retry?: boolean }
): Promise<string> {
  const nativeLanguage = getLanguageName(nativeLanguageCode);
  const pronouncePrompt = `The expression "${expression}", in ${nativeLanguage} syllables, is pronounced: `;
  const pronounceMessage: Message = { role: 'user', content: pronouncePrompt };
  const pronounce = await completions([pronounceMessage], options);
  return pronounce.content;
}

async function breakdown(
  expression: string,
  nativeLanguageCode: LanguageCode,
  options?: { retry?: boolean }
): Promise<string> {
  const nativeLanguage = getLanguageName(nativeLanguageCode);
  const breakdownPrompt = `In less that 25 words, break down the following expression word by word, each word on it's own line, in <word>|<pronunciation using ${nativeLanguage} syllabes>|<${nativeLanguage} definition/usage>: ${expression}`;
  const breakdown = await completions([{ role: 'user', content: breakdownPrompt }], options);
  return breakdown.content;
}

export interface AttachmentItem {
  name: LocalizedValues;
  value: string;
  audio?: {
    index: number;
    rate?: number;
  };
};

export type Link = FailureLink | OverviewLink;

interface FailureLink {
  name: LocalizedValues;
  type: 'failure',
  reason: AttachmentItem;
};

interface OverviewLink {
  name: LocalizedValues;
  type: 'overview',
  expression: string;
  index: number;
};



const SOUNDS: Record<NonNullable<Sounds>, string> = {
  play: playAudio,
  right: rightAudio,
  wrong: wrongAudio,
  final: finalAudio,
  finale: finaleAudio,
  sent: sentAudio,
}


export type Message = {
  role: 'user' | 'system' | 'assistant';
  content: string;
};

export type Tapback = {
  type: 'heart' | 'error' | 'thumbsup' | 'text';
  text?: string;
  detail?: string;
};

type ConversationGetter = () => Conversation;

type Expression = {
  native?: string;
  value: string;
  pronunciation?: string;
  breakdown?: string;
  reviewStage?: number;
  lastSuccessTime?: string;
  lastSuccessRole?: ChallengeSuccessRole | null;
  repeatFailCount?: number;
  translateFailCount?: number;
  pronunciationScores?: number[];
};

const FAILED_TO_LOAD_EXPRESSION: Readonly<Expression> = Object.freeze({
  value: 'Failed to load.',
  native: 'Failed to load.',
  pronunciation: '',
  breakdown: '',
  reviewStage: 0,
  lastSuccessRole: null,
  lastSuccessTime: '',
  repeatFailCount: 0,
  translateFailCount: 0,
  pronunciationScores: [],
});

type PhraseRequest = {
  request: string;
  indices: number[];
  time: string;
};

type ActivityEvent = {
  start: string;
  durationMs: number;
};

let _lastTimeMs = 0;
const MONOTONIC_TIME = (): string => {
  const now = Date.now();
  const timeMs = Math.max(now, _lastTimeMs + 1);
  _lastTimeMs = timeMs;
  return new Date(timeMs).toISOString();
};

function checkForKey(key: string, obj: { [key: string]: unknown }) {
  if (obj[key] == null) {
    writeError(`Missing key: ${key}`);
  }
}

function checkForNonEmptyArray(key: string, obj: { [key: string]: unknown }) {
  const value = obj[key];
  if (!Array.isArray(value) || value.length === 0) {
    writeError(`Missing non-empty array: ${key}`);
  }
}

const PRONUNCIATION_THRESHOLD = 0.80;

async function evaluatePronunciation(
  conversation: Conversation,
  target: string,
  attempt: string,
): Promise<
  { passed: boolean; score: number; feedback: string }
> {
  const { score, feedback } = await pronunciationAssessment(
    target,
    attempt,
    conversation.nativeLanguageCode,
    conversation.languageCode,
  );
  const heuristicScore = pronunciationScore(target, attempt, conversation.languageCode);
  const bestScore = Math.max(score, heuristicScore);

  if (bestScore >= PRONUNCIATION_THRESHOLD) {
    return { passed: true, score: bestScore, feedback };
  }
  return { passed: false, score: bestScore, feedback };
}

const nextRoleFor = (conversation: Conversation, index: number, messages = conversation._messages): ChallengeRole => (
  !conversation.expressionHas('repeat_success', index, messages)
    ? 'initial_repeat'
    : conversation.expressionHas('meaning_success', index, messages)
      ? 'translate'
      : 'repeat'
);

const isChallengeMessage = (event: MessageEvent): event is ChallengeMessage => (
  event.role === 'initial_repeat' ||
  event.role === 'repeat' ||
  event.role === 'meaning' ||
  event.role === 'translate'
);

const getLessonIndices = (conversation: Conversation, lessonId: string): number[] => {
  const lesson = conversation.getMessageById(lessonId, 'lesson');
  return lesson?.indices ?? [];
};

const getLessonEvents = (messages: MessageEvent[], lessonId: string): MessageEvent[] => {
  const start = messages.findIndex(
    (event) => event.role === 'lesson' && event.id === lessonId
  );
  if (start < 0) {
    return [];
  }
  const end = messages.findIndex(
    (event, index) => index > start && event.role === 'lesson'
  );
  return end < 0
    ? messages.slice(start + 1)
    : messages.slice(start + 1, end);
};

const getLessonCompletedIndices = (conversation: Conversation, lessonId: string): number[] => {
  const completed = new Set<number>();
  for (const event of getLessonEvents(conversation._messages, lessonId)) {
    if (event.role === 'translate_success') {
      completed.add(event.index);
    }
  }
  for (const index of conversation.removedExpressions) {
    if (getLessonIndices(conversation, lessonId).includes(index)) {
      completed.add(index);
    }
  }
  return [...completed];
};

type ChallengeRequiredProps = Pick<ChallengeMessage, 'index' | 'parentId'>;
type ChallengeProps = ChallengeRequiredProps & Partial<ChallengeMessage>

const nextChallenge = (
  conversation: Conversation,
  challenge: ChallengeProps,
): MessageEvent[] => {
  const { index, parentId } = challenge;
  const indices = conversation.getMessageById(parentId, 'lesson')?.indices ?? [];
  const completedIndices = getLessonCompletedIndices(conversation, parentId);
  // Only cycle through expressions that are still incomplete in this session.
  const remainingIndices = indices.filter((i) => completedIndices.indexOf(i) === -1);
  if (remainingIndices.length === 0) {
    return [conversation.entryMessage()];
  }
  const currentPos = indices.indexOf(index);
  if (currentPos < 0) {
    return [conversation.entryMessage()];
  }
  let nextIndex = remainingIndices[0];
  for (let offset = 1; offset <= indices.length; offset += 1) {
    const candidate = indices[(currentPos + offset) % indices.length];
    if (remainingIndices.includes(candidate)) {
      nextIndex = candidate;
      break;
    }
  }
  return [
    new ChallengeMessage(() => conversation, {
      role: nextRoleFor(conversation, nextIndex),
      index: nextIndex,
      parentId
    })
  ];
}

const finishChallenge = (
  conversation: Conversation,
  challenge: ChallengeProps,
): MessageEvent[] => {
  const { index, parentId } = challenge;
  const indices = getLessonIndices(conversation, parentId);
  const completedIndices = getLessonCompletedIndices(conversation, parentId);
  const nextCompleted = completedIndices.includes(index)
    ? completedIndices
    : completedIndices.concat(index);
  const allDone = indices.length > 0 && indices.every((value) => nextCompleted.includes(value));
  if (allDone) {
    return [
      new OutcomeMessage(() => conversation, { role: 'success', parentId }),
      conversation.entryMessage(),
    ];
  }
  return nextChallenge(conversation, challenge);
};

const onComplete = (conversation: Conversation): MessageEvent[] => {
  const last = conversation._messages[conversation._messages.length - 1];

  if (
    last.role === 'repeat_success' ||
    last.role === 'meaning_success' ||
    last.role === 'translate_success'
  ) {
    const expression = conversation.getMutableExpression(last.index);
    if (expression) {
      const currentRole = expression.lastSuccessRole;
      const currentTime = expression.lastSuccessTime ?? '';
      const roleWins = !currentRole || SUCCESS_ROLE_RANK[last.role] > SUCCESS_ROLE_RANK[currentRole];
      const timeWins = last.role === currentRole && last.time > currentTime;
      if (roleWins || timeWins) {
        expression.lastSuccessRole = last.role;
        expression.lastSuccessTime = last.time;
      }
      if (last.role === 'translate_success') {
        const challenge = conversation.getMessageById(last.parentId);
        const lessonId = challenge && isChallengeMessage(challenge) ? challenge.parentId : null;
        const lessonEvents = lessonId ? getLessonEvents(conversation._messages, lessonId) : [];
        const hasRepeatFail = lessonEvents.some(
          (event) => event.role === 'repeat_fail' && event.index === last.index
        );
        if (!hasRepeatFail) {
          const currentStage = expression.reviewStage ?? 0;
          expression.reviewStage = Math.min(currentStage + 1, REVIEW_INTERVALS_MS.length - 1);
        }
      }
    }
  }
  if (last.role === 'repeat_fail') {
    const expression = conversation.getMutableExpression(last.index);
    if (expression) {
      const challenge = conversation.getMessageById(last.parentId);
      const isTranslateFail = !!(challenge && isChallengeMessage(challenge) && challenge.role === 'translate');
      if (isTranslateFail) {
        expression.translateFailCount = (expression.translateFailCount ?? 0) + 1;
      } else {
        expression.repeatFailCount = (expression.repeatFailCount ?? 0) + 1;
        const scores = expression.pronunciationScores ?? [];
        scores.push(last.pronunciationScore);
        if (scores.length > 25) {
          scores.splice(0, scores.length - 25);
        }
        expression.pronunciationScores = scores;
      }
    }
  }

  if (last.role === 'breakdown_answer') {
    const breakdown = conversation.getMessageById(last.parentId, 'breakdown_prompt');
    const parent = breakdown ? conversation.getMessageById(breakdown.parentId) : null;
    if (parent && isChallengeMessage(parent)) {
      return [
        new ChallengeMessage(() => conversation, {
          role: parent.role,
          index: parent.index,
          attempt: parent.attempt,
          parentId: parent.parentId,
        }),
      ];
    }
    return [conversation.entryMessage()];
  }
  if (last.role === 'lesson') {
    const lastAskNextIndex = conversation._messages
      .findLastIndex((m, i, list) => m.role === 'ask_next' && list[i - 1]?.role === 'success');
    const pruned = conversation._messages.splice(0, lastAskNextIndex);
    const recordingIds = pruned
      .filter((message) => message.role === 'user')
      .map((message) => message.id);
    void audio.purgeRecordings(recordingIds);

    const { indices } = last;
    const nextIndex = indices[0];
    return [
      new ChallengeMessage(() => conversation, {
        role: nextRoleFor(conversation, nextIndex),
        index: nextIndex,
        parentId: last.id,
      }),
    ];
  }
  if (last.role === 'conversation') {
    return [];
  }

  const message = conversation.currentChallenge;
  if (message) {
    const { attempt, index, role, parentId } = message;
    const challengeProps = { index, role, parentId };

    if ((last.role === 'repeat_success' || last.role === 'translate_success' || last.role === 'meaning_success')) {
      if (role === 'repeat') {
        return [
          new ChallengeMessage(() => conversation, { ...challengeProps, role: 'meaning' }),
        ]
      }

      if (role === 'translate' && attempt === 1) {
        return finishChallenge(conversation, message);
      }

      return nextChallenge(conversation, message);
    }

    if (last.role === 'repeat_fail') {
      if (attempt > 1 && (attempt - 1) % 5 === 0) {
        return [
          new AskContinueRepeatMessage(() => conversation, { parentId }),
        ];
      }

      return [
        new ChallengeMessage(() => conversation, { ...challengeProps, attempt: attempt + 1 }),
      ];
    }

    if (last.role === 'meaning_fail') {
      return nextChallenge(conversation, message);
    }

    if (last.role === 'continue_no') {
      return nextChallenge(conversation, message);
    }

    if (last.role === 'continue_yes') {
      return [
        new ChallengeMessage(() => conversation, { ...challengeProps, attempt: attempt + 1 }),
      ];
    }
  }

  return [];
}

type Sounds = 'play' | 'right' | 'wrong' | 'final' | 'finale' | 'sent';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAYS_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAYS_MS;
const REVIEW_INTERVALS_MS = [
  2 * MINUTE_MS,
  10 * MINUTE_MS,
  60 * MINUTE_MS,
  6 * HOUR_MS,
  24 * HOUR_MS,
  3 * DAYS_MS,
  WEEK_MS,
  4 * WEEK_MS,
  12 * WEEK_MS,
];

class AssistantMessage {
  event: MessageEvent;
  playCount = 0;
  role: 'assistant' = 'assistant';
  rate = 1;
  languageCode: LanguageCode = 'en';
  size?: "5" | "7" | "9";
  sound?: Sounds;
  links?: Link[];
  loaded?: Promise<void>;
  content: string;
  removeRestoreButton = false;
  breakdownButton = false;
  get playable(): boolean {
    return this.languageCode !== this.getConversation().nativeLanguageCode;
  }
  silent = false;

  constructor(
    event: MessageEvent,
    public getConversation: () => Conversation,
    value: Partial<AssistantMessage>,
  ) {
    this.event = event;
    checkForKey('content', value);
    this.content = value.content!;
    Object.assign(this, value);
  }

  get messages(): ViewMessage[] {
    return [this];
  }

  async play(): Promise<void> {
    if (this.silent) {
      return;
    }

    const audioUrl = this.sound ? SOUNDS[this.sound] : await speak(this.content);
    this.playCount = 1;

    if (this.sound) {
      await audio.play(audioUrl, { duration: 0.4, cache: true, context: { message: this.content, role: this.role, eventId: this.event?.id } })
    } else {
      const rate = this.role === 'assistant' ? this.rate : 1;
      await audio.play(audioUrl, { rate, context: { message: this.content, role: this.role, eventId: this.event?.id } });
      await wait(100);
    }
  }
}

class UserMessage {
  id = crypto.randomUUID ? crypto.randomUUID() : String(Math.random());
  event: MessageEvent;
  role: 'user' = 'user';
  playCount = 0;
  time = MONOTONIC_TIME();
  content: string;
  parentId: string;
  error: boolean = false;
  get playable(): boolean {
    return audio.hasRecording(this.id);
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<UserMessage>,
  ) {
    this.event = this;
    checkForKey('content', value);
    this.content = value.content!;
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    Object.assign(this, value);
  }

  get messages(): ViewMessage[] {
    return [this];
  }

  get tapbacks(): Tapback[] {
    var text, detail;
    const conversation = this.getConversation();
    const tapbacks = conversation._messages
      .flatMap((a) => a.messages)
      .filter((a): a is TapbackMessage => a.role === 'tapback' && a.userMessageId === this.id)
      .map(a => ({ type: a.type, text: a.text, detail: a.detail }));
    if (this.error) {
      tapbacks.push({ type: 'error', text, detail });
    }
    return tapbacks;
  }

  async play(): Promise<void> {
    this.playCount = 1;
    await audio.play(sentAudio, { cache: true, duration: 0.35 });
  }
}

class TapbackMessage {
  role: 'tapback' = 'tapback';
  userMessageId: string;
  type: Tapback['type'];
  text?: string;
  detail?: string;
  silent?: boolean;
  playCount = 0;

  constructor(value: Partial<TapbackMessage>) {
    checkForKey('userMessageId', value);
    checkForKey('type', value);
    this.userMessageId = value.userMessageId!;
    this.type = value.type!;
    Object.assign(this, value);
  }

  get messages(): ViewMessage[] {
    return [this];
  }

  async play(): Promise<void> {
    this.playCount = 1;
    if (this.silent) {
      return;
    }
    if (this.type === 'heart') {
      // if (hearts > 0 && hearts % 5 === 0) {
      //   // this.set({ shock: true });
      //   // setTimeout(() => this.set({ shock: false }), 1000);
      //   await audio.play(wowAudio, { duration: 0.1, cache: true });
      // }
      await audio.play(loveAudio, { duration: 0.25, cache: true });
    } else {
      await audio.play(tapbackAudio, { duration: 0.25, cache: true });
    }
  }
}

export type PromptMessage = UserMessage | AssistantMessage;
type ViewMessage = PromptMessage | TapbackMessage;

class AskNextMessage {
  role: 'ask_next' = 'ask_next';
  playCount = 0;
  parentId?: string;
  time = MONOTONIC_TIME();
  responseType: 'en' = 'en';
  reviewCount?: number;
  isReviewFollowup?: boolean;
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<AskNextMessage>
  ) {
    Object.assign(this, value);
    if (this.reviewCount == null || this.isReviewFollowup == null) {
      const conversation = this.getConversation();
      const reviewCount = conversation.reviewableExpressions.length;
      const messageIndex = conversation._messages.findIndex((m) => m === this);
      const previous = messageIndex > 0 ? conversation._messages[messageIndex - 1] : null;
      this.reviewCount = reviewCount;
      this.isReviewFollowup = !!previous && previous.role === 'success' && reviewCount > 0;
    }
  }

  async next(userMessage: UserMessage): Promise<MessageEvent[]> {
    const conversation = this.getConversation();
    const input = userMessage.content;
    const reviewCandidates = conversation.reviewableExpressions.slice(0, 4);
    const messageIndex = conversation._messages.findIndex((m) => m === this);
    const previous = messageIndex > 0 ? conversation._messages[messageIndex - 1] : null;
    const isReviewFollowup = !!previous && previous.role === 'success' && reviewCandidates.length > 0;
    const shouldReview = reviewCandidates.length > 0 && (
      wantsReview(input) || (isReviewFollowup && await wantsChallengeContinue(input))
    );
    console.log('AskNextMessage.next', { input, reviewCandidates });
    if (shouldReview) {
      return [
        new LessonMessage(() => conversation, {
          indices: reviewCandidates,
          parentId: this.id,
        }),
      ];
    }
    if (wantsVariations(input)) {
      const matchedExpressions = getVariationBaseExpressions(conversation, input).slice(0, 4);
      const baseExpressions = matchedExpressions.length
        ? matchedExpressions
        : conversation.reviewableExpressions.slice(0, 4)
          .map((index) => conversation.getExpression(index));
      console.log({ baseExpressions, matchedExpressions });
      if (baseExpressions.length === 0) {
        const recentExpressions = conversation.expressions.slice(-4);
        if (recentExpressions.length === 0) {
          return [new AskNextMessage(() => conversation, {})];
        }
        baseExpressions.push(...recentExpressions);
        console.log({ recentExpressions });
      }
      const variationPrompt = buildIntentPrompt(
        conversation,
        'Create variation of each phrases replacing either noun or verb.',
        `User:\n${input}\nPhrases:\n${baseExpressions.map(e => e.value).join('\n')}\n`,
      );
      const variationResponse = await generate(variationPrompt);
      const variations = await expressionsFromDialog(conversation, variationResponse.content);
      if (variations.length === 0) {
        return [new AskNextMessage(() => conversation, {})];
      }
      return [
        new PhrasesMessage(() => conversation, {
          parentId: this.id,
          request: input,
          expressions: variations,
        }),
      ];
    }
    if (wantsConversation(input)) {
      return [new ConversationMessage(() => conversation, { parentId: this.id })];
    }
    if (wantsPhrases(input)) {
      const intentPrompt = buildIntentPrompt(conversation, input);
      const intentResponse = await generate(intentPrompt);
      const newExpressions = await expressionsFromDialog(conversation, intentResponse.content);
      if (newExpressions.length === 0) {
        return [new AskNextMessage(() => conversation, {})];
      }
      return [
        new PhrasesMessage(() => conversation, {
          parentId: this.id,
          request: input,
          expressions: newExpressions,
        }),
      ];
    }
    if (isReviewFollowup) {
      return [new AskNextMessage(() => conversation, {})];
    }
    return [];
  }

  get messages(): ViewMessage[] {
    const conversation = this.getConversation();
    const language = getLanguageName(conversation.languageCode);
    const reviewCount = this.reviewCount ?? 0;
    const isReviewFollowup = !!this.isReviewFollowup;
    const reviewText = reviewCount > 0 ? 'review, ' : '';
    const text = isReviewFollowup
      ? 'Review more phrases?'
      : `Would you like to ${reviewText}have a conversation, or learn either new or variations of phrases in ${language}?`;
    return [
      new AssistantMessage(this, this.getConversation, {
        content: text,
      }),
    ];
  }
}

class PhrasesMessage {
  role: 'phrases' = 'phrases';
  playCount = 0;
  parentId?: string;
  request: string;
  expressions?: Expression[];
  time = MONOTONIC_TIME();
  responseType: 'en' = 'en';
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<PhrasesMessage>
  ) {
    checkForKey('request', value);
    this.request = value.request!;
    Object.assign(this, value);
  }

  async next(userMessage: UserMessage): Promise<MessageEvent[]> {
    const conversation = this.getConversation();
    if (this.expressions && this.expressions.length > 0 && this.request) {
      if (wantsPhraseConfirmation(userMessage.content)) {
        const requests: string[] = [];
        for (let i = conversation._messages.length - 1; i >= 0; i -= 1) {
          const message = conversation._messages[i];
          if (message.role === 'ask_next') {
            break;
          }
          if (message.role === 'phrases') {
            requests.unshift(message.request);
          }
        }
        const requestHistory = requests.join('\n');
        const indices = conversation.addExpressions(this.expressions);
        conversation.addPhraseRequest(requestHistory || this.request, indices);
        return [
          new LessonMessage(() => conversation, {
            indices,
            parentId: this.id,
          }),
        ];
      }
    }

    let historyText = '';
    for (let i = conversation._messages.length - 1; i >= 0; i -= 1) {
      const message = conversation._messages[i];
      if (message.role === 'ask_next') {
        break;
      }
      if (message.role === 'phrases' && message.expressions) {
        const phrases = message.expressions.map(e => e.value).join('\n');
        historyText = `User:\n${message.request}\nPhrases:\n${phrases}\n` + historyText;
      }
    }
    const prompt = buildIntentPrompt(conversation, userMessage.content, historyText);
    // console.log('PHRASES BABY!', JSON.stringify(prompt.map(a => a.content)));
    const response = await generate(prompt);
    const refreshedExpressions = await expressionsFromDialog(conversation, response.content);
    const expressions = refreshedExpressions.length ? refreshedExpressions : this.expressions;
    if (!expressions || expressions.length === 0) {
      return [new AskNextMessage(this.getConversation, {})];
    }

    return [
      new PhrasesMessage(() => conversation, {
        parentId: this.id,
        request: userMessage.content,
        expressions,
      }),
    ];
  }

  get messages(): ViewMessage[] {
    const conversation = this.getConversation();
    const language = getLanguageName(conversation.languageCode);
    if (this.expressions && this.expressions.length > 0) {
      const lines = this.expressions.map((expression, index) => {
        const native = expression.native ? ` (${expression.native})` : '';
        return `${index + 1}. ${expression.value}${native}`;
      });
      return [
        new AssistantMessage(this, this.getConversation, {
          content: 'Here are the phrases:'
        }),
        new AssistantMessage(this, this.getConversation, {
          content: lines.join('\n'),
          silent: true,
        }),
        new AssistantMessage(this, this.getConversation, {
          content: 'Do these look right? Reply "yes" or tell me what to change.'
        }),
      ];
    }
    return [
      new AssistantMessage(this, this.getConversation, {
        content: `What would you like to say in ${language}?`,
      }),
    ];
  }
}

class ConversationMessage {
  role: 'conversation' = 'conversation';
  playCount = 0;
  time = MONOTONIC_TIME();
  responseType: LanguageCode = 'en';
  content = '';
  parentId: string;
  stage: 'proposal' | 'confirm' | 'ready' = 'proposal';
  place = '';
  person = '';
  reason = '';
  plan = '';
  note = '';
  badResponseCount = 0;
  expressionIndex?: number;
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<ConversationMessage>
  ) {
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    Object.assign(this, value);
    const conversation = this.getConversation();
    this.responseType = this.stage === 'ready'
      ? conversation.languageCode
      : conversation.nativeLanguageCode;
    if (!this.place || !this.person || !this.reason || !this.plan) {
      const scenario = this.buildDefaultScenario();
      this.place = this.place || scenario.place;
      this.person = this.person || scenario.person;
      this.reason = this.reason || scenario.reason;
      this.plan = this.plan || scenario.plan;
    }
  }

  get messages(): ViewMessage[] {
    const conversation = this.getConversation();
    const language = getLanguageName(conversation.languageCode);
    if (this.stage === 'proposal' || this.stage === 'confirm') {
      const lines = [
        `Scenario (${language}):`,
        `- Place: ${this.place}`,
        `- Person: ${this.person}`,
        `- Reason: ${this.reason}`,
      ];
      const prompt = this.note
        ? `${this.note} Tell me what to change, or reply "yes" to start.`
        : 'Does this work? Tell me what to change, or reply "yes" to start.';
      return [
        new AssistantMessage(this, this.getConversation, {
          content: lines.join('\n'),
          languageCode: conversation.nativeLanguageCode,
          silent: true,
        }),
        new AssistantMessage(this, this.getConversation, {
          content: prompt,
          languageCode: conversation.nativeLanguageCode,
        }),
      ];
    }
    const messages: ViewMessage[] = [];
    if (this.note) {
      messages.push(new AssistantMessage(this, this.getConversation, {
        content: this.note,
        languageCode: conversation.nativeLanguageCode,
      }));
    }
    messages.push(new AssistantMessage(this, this.getConversation, {
      content: this.content,
      languageCode: conversation.languageCode,
      links: typeof this.expressionIndex === 'number' ? [{
        name: 'Details',
        type: 'overview',
        expression: this.content,
        index: this.expressionIndex,
      }] : undefined,
    }));
    return messages;
  }

  async next(userMessage: UserMessage): Promise<MessageEvent[]> {
    const conversation = this.getConversation();
    if (this.stage === 'proposal' || this.stage === 'confirm') {
      const input = userMessage.content.trim();
      const lower = input.toLowerCase();
      const isYes = /^(yes|yep|yeah|ok|okay|sure|sounds good|looks good)/i.test(lower);
      const wantsChange = /(change|edit|update|different|instead|swap|make it|set it)/i.test(lower);
      if (isYes) {
        const plan = await this.buildPlan(conversation);
        const question = await generateConversationQuestion(conversation, '', plan);
        const existingIndex = conversation.expressions.findIndex((expression) => expression.value === question);
        const nextIndex = existingIndex >= 0
          ? existingIndex
          : conversation.addExpressions([{ value: question, native: '', repeatFailCount: 0, translateFailCount: 0, pronunciationScores: [] }])[0];
        return [new ConversationMessage(() => conversation, {
          content: question,
          parentId: this.parentId,
          stage: 'ready',
          place: this.place,
          person: this.person,
          reason: this.reason,
          plan,
          expressionIndex: nextIndex,
        })];
      }
      if (!input || (!wantsChange && input.split(/\s+/).length < 4)) {
        return [new ConversationMessage(() => conversation, {
          parentId: this.parentId,
          stage: 'confirm',
          place: this.place,
          person: this.person,
          reason: this.reason,
          plan: this.plan,
          note: 'I’m not sure what you want to change.',
        })];
      }

      const historyText = this.buildPlanningHistory();
      const updated = await this.applyEditsWithHistory(input, historyText);
      return [new ConversationMessage(() => conversation, {
        parentId: this.parentId,
        stage: 'confirm',
        place: updated.place,
        person: updated.person,
        reason: updated.reason,
        plan: updated.plan,
      })];
    }

    const classification = await this.classifyResponse(userMessage);
    if (classification.result !== 'VALID') {
      const nextBadCount = this.badResponseCount + 1;
      if (nextBadCount >= 3) {
        return [
          new ConversationMessage(() => conversation, {
            parentId: this.parentId,
            stage: 'ready',
            content: this.content,
            place: this.place,
            person: this.person,
            reason: this.reason,
            plan: this.plan,
            note: "Let's stop here.",
            badResponseCount: nextBadCount,
            expressionIndex: this.expressionIndex,
          }),
          ...this.buildConversationLesson(conversation, this.id),
        ];
      }
      const steerLine = await this.generateSteeringLine(classification.result, classification.mistakes);
      const existingIndex = conversation.expressions.findIndex((expression) => expression.value === steerLine);
      const nextIndex = existingIndex >= 0
        ? existingIndex
        : conversation.addExpressions([{ value: steerLine, native: '', repeatFailCount: 0, translateFailCount: 0, pronunciationScores: [] }])[0];
      return [new ConversationMessage(() => conversation, {
        parentId: this.parentId,
        stage: 'ready',
        content: steerLine,
        place: this.place,
        person: this.person,
        reason: this.reason,
        plan: this.plan,
        badResponseCount: nextBadCount,
        expressionIndex: nextIndex,
      })];
    }

    if (await this.isGoalReached(userMessage)) {
      return this.buildConversationLesson(conversation, this.id);
    }

    const question = await generateConversationQuestion(conversation, userMessage.content, this.plan);
    const existingIndex = conversation.expressions.findIndex((expression) => expression.value === question);
    const nextIndex = existingIndex >= 0
      ? existingIndex
      : conversation.addExpressions([{ value: question, native: '', repeatFailCount: 0, translateFailCount: 0, pronunciationScores: [] }])[0];
    return [new ConversationMessage(() => conversation, {
      content: question,
      parentId: this.parentId,
      stage: 'ready',
      place: this.place,
      person: this.person,
      reason: this.reason,
      plan: this.plan,
      badResponseCount: 0,
      expressionIndex: nextIndex,
    })];
  }

  private buildDefaultScenario(): { place: string; person: string; reason: string; plan: string } {
    const places = ['a coffee shop', 'a train station', 'a hotel lobby', 'a bookstore', 'a clinic'];
    const people = ['a barista', 'a ticket agent', 'a front desk clerk', 'a shop assistant', 'a nurse'];
    const reasons = [
      'you need a simple request',
      'you need directions',
      'you need a small fix',
      'you want a recommendation',
      'you need to confirm a detail',
    ];
    const plans = [
      'You ask questions; they answer.',
      'You ask for directions; they answer.',
      'You ask for options; they answer.',
      'You ask to confirm a detail; they answer.',
      'You ask for help; they answer.',
    ];
    const index = Math.abs(this.time.split('').reduce((a, c) => a + c.charCodeAt(0), 0));
    return {
      place: places[index % places.length],
      person: people[index % people.length],
      reason: reasons[index % reasons.length],
      plan: plans[index % plans.length],
    };
  }

  private async buildPlan(conversation: Conversation): Promise<string> {
    const language = getLanguageName(conversation.languageCode);
    const prompt = `Create a concise conversation plan in ${language} for a beginner. ` +
      `Use this scenario: place="${this.place}", person="${this.person}", reason="${this.reason}". ` +
      `Plan format:\n` +
      `- Opening question from the other person (do NOT include options in the question).\n` +
      `- Options: 2-4 possible user responses as a short comma-separated list.\n` +
      `- Goal: a clear goal that ends the conversation once achieved.\n` +
      `Keep it short.`;
    const response = await generate([{ role: 'user', content: prompt }]);
    console.log('buildPlan response', response.content);
    return response.content.trim() || 'Opening question: What would you like?\nOptions: coffee, tea\nGoal: finished ordering';
  }

  private buildConversationHistory(): string {
    const conversation = this.getConversation();
    let historyText = '';
    const end = conversation._messages.length - 1;
    for (let i = end; i >= 0; i -= 1) {
      const message = conversation._messages[i];
      if (message.role === 'ask_next') {
        break;
      }
      if (message.role === 'conversation' && message.stage === 'ready') {
        historyText = `Assistant: ${message.content}\n` + historyText;
      }
      if (message.role === 'user' && i < end) {
        historyText = `User: ${message.content}\n` + historyText;
      }
    }
    return historyText.trim();
  }

  private getConversationExpressionIndices(): number[] {
    const conversation = this.getConversation();
    const indices: number[] = [];
    for (const message of conversation._messages) {
      if (message.role === 'conversation' && message.stage === 'ready') {
        const entry = message as ConversationMessage;
        if (entry.parentId !== this.parentId) {
          continue;
        }
        const index = entry.expressionIndex;
        if (typeof index === 'number') {
          indices.push(index);
        }
      }
    }
    return Array.from(new Set(indices));
  }

  private buildConversationLesson(conversation: Conversation, parentId: string): MessageEvent[] {
    const indices = this.getConversationExpressionIndices();
    if (indices.length === 0) {
      return [new AskNextMessage(() => conversation, {})];
    }
    return [new LessonMessage(() => conversation, { indices, parentId })];
  }


  private async classifyResponse(
    userMessage: UserMessage
  ): Promise<{ result: 'VALID' | 'UNRELATED' | 'INCOHERENT'; mistakes: string }> {
    const historyText = this.buildConversationHistory();
    const prompt = [
      `You are classifying a learner response.`,
      `Plan:\n${this.plan}`,
      historyText ? `Dialog so far:\n${historyText}` : '',
      `Response: ${userMessage.content}`,
      `Output either VALID, UNRELATED, or INCOHERENT followed by a pipe and a description of mistakes or suggestions.`,
      `Ex: VALID|This word is incorrect.`,
    ].filter(Boolean).join('\n');
    const response = await generate([{ role: 'user', content: prompt }]);
    const [resultText, mistakes] = response.content.trim().split('|');
    const resultLine = resultText.toUpperCase();
    const result = resultLine.includes('VALID')
      ? 'VALID'
      : resultLine.includes('UNRELATED')
        ? 'UNRELATED' : 'INCOHERENT';
    return { result, mistakes };
  }

  private async generateSteeringLine(
    classification: 'UNRELATED' | 'INCOHERENT',
    mistakes: string
  ): Promise<string> {
    const conversation = this.getConversation();
    const language = getLanguageName(conversation.languageCode);
    const scene = `Place: ${this.place}\nPerson: ${this.person}\nReason: ${this.reason}`;
    const goal = this.plan.match(/Goal:\s*(.+)/i)?.[1]?.trim() ?? '';
    const historyText = this.buildConversationHistory();
    const prompt = [
      `You are the ${this.person} at ${this.place}.`,
      `Speak only in ${language}.`,
      `Respond with ONE short sentence.`,
      `Scene:\n${scene}`,
      goal ? `Goal: ${goal}` : '',
      historyText ? `Dialog so far:\n${historyText}` : '',
      `User response was classified as ${classification}.`,
      mistakes ? `Mistakes/suggestions: ${mistakes}` : '',
      `If UNRELATED, steer back to the topic.`,
      `If INCOHERENT, be confused and ask again more simply.`,
      `Do not mention that you are correcting them.`,
    ].filter(Boolean).join('\n');
    const response = await generate([{ role: 'user', content: prompt }]);
    return response.content.trim() || this.content;
  }

  private async isGoalReached(userMessage: UserMessage): Promise<boolean> {
    const goal = this.plan.match(/Goal:\s*(.+)/i)?.[1]?.trim();
    if (!goal) {
      return false;
    }
    const historyText = this.buildConversationHistory();
    const prompt = [
      `Decide if the conversation goal has been achieved.`,
      `Goal: ${goal}`,
      historyText ? `Dialog so far:\n${historyText}User: ${userMessage.content}\n` : '',
      `Answer only YES or NO.`,
    ].filter(Boolean).join('\n');
    const response = await generate([{ role: 'user', content: prompt }]);
    return response.content.trim().toUpperCase().startsWith('YES');
  }

  private buildPlanningHistory(): string {
    const conversation = this.getConversation();
    let historyText = '';
    for (let i = conversation._messages.length - 1; i >= 0; i -= 1) {
      const message = conversation._messages[i];
      if (message.role === 'ask_next') {
        break;
      }
      if (message.role === 'conversation' && (message.stage === 'proposal' || message.stage === 'confirm')) {
        const scenario = [
          `Place: ${message.place}`,
          `Person: ${message.person}`,
          `Reason: ${message.reason}`,
        ].join('\n');
        historyText = `Assistant:\n${scenario}\n` + historyText;
      }
      if (message.role === 'user') {
        historyText = `User:\n${message.content}\n` + historyText;
      }
    }
    return historyText.trim();
  }

  private async applyEditsWithHistory(
    input: string,
    historyText: string
  ): Promise<{ place: string; person: string; reason: string; plan: string }> {
    const prompt = [
      `You are updating a conversation scenario.`,
      `Current scenario:`,
      `Place: ${this.place}`,
      `Person: ${this.person}`,
      `Reason: ${this.reason}`,
      historyText ? `History:\n${historyText}` : '',
      `User feedback: ${input}`,
      `Return the updated scenario as three lines for place, person, reason to talk.`,
    ].filter(Boolean).join('\n');
    const response = await generate([{ role: 'user', content: prompt }]);
    const text = response.content || '';
    const placeMatch = (text.match(/Place:\s*(.+)/i) || [])[1]?.trim();
    const personMatch = (text.match(/Person:\s*(.+)/i) || [])[1]?.trim();
    const reasonMatch = (text.match(/Reason:\s*(.+)/i) || [])[1]?.trim();
    const fallbackLines = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 3);
    const place = placeMatch || fallbackLines[0] || this.place;
    const person = personMatch || fallbackLines[1] || this.person;
    const reason = reasonMatch || fallbackLines[2] || this.reason;
    return { place, person, reason, plan: this.plan };
  }
}

class BreakdownMessage {
  role: 'breakdown_prompt' | 'breakdown_answer' = 'breakdown_prompt';
  playCount = 0;
  time = MONOTONIC_TIME();
  responseType: LanguageCode = 'en';
  parentId: string;
  index: number;
  answer?: string;
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<BreakdownMessage>
  ) {
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    checkForKey('index', value);
    this.index = value.index!;
    Object.assign(this, value);
    if (this.role === 'breakdown_answer') {
      checkForKey('answer', value);
      this.answer = value.answer as string;
    }
    this.responseType = this.getConversation().nativeLanguageCode;
  }

  get messages(): ViewMessage[] {
    const conversation = this.getConversation();
    const expression = conversation.getExpression(this.index);
    const label = expression.value || expression.native || 'Phrase not found!';
    if (this.role === 'breakdown_answer') {
      return [
        new AssistantMessage(this, this.getConversation, {
          content: this.answer ?? '',
          languageCode: conversation.nativeLanguageCode,
          silent: true,
        }),
      ];
    }
    return [
      new AssistantMessage(this, this.getConversation, {
        content: `Ask about: ${label}`,
      }),
    ];
  }

  async next(userMessage: UserMessage): Promise<MessageEvent[]> {
    if (this.role === 'breakdown_answer') {
      return [];
    }
    const conversation = this.getConversation();
    const answer = await generateBreakdownAnswer(conversation, this.index, userMessage.content);
    return [
      new BreakdownMessage(() => conversation, {
        parentId: this.id,
        index: this.index,
        answer,
        role: 'breakdown_answer',
      }),
    ];
  }
}

class LessonMessage {
  role: 'lesson' = 'lesson';
  playCount = 0;
  time = MONOTONIC_TIME();
  responseType: 'en' = 'en';
  indices: number[];
  startingRoles: (ChallengeSuccessRole | null)[];
  parentId: string;
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<LessonMessage>
  ) {
    checkForKey('indices', value);
    checkForNonEmptyArray('indices', value);
    this.indices = value.indices!;
    const conversation = this.getConversation();
    this.startingRoles = this.indices.map((index) => (
      conversation.getExpression(index).lastSuccessRole ?? null
    ));
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    Object.assign(this, value);
  }

  async next(): Promise<MessageEvent[]> {
    return [];
  }

  get messages(): ViewMessage[] {
    return [];
  }
}

class AskContinueRepeatMessage {
  role: 'ask_continue_repeat' = 'ask_continue_repeat';
  playCount = 0;
  time = MONOTONIC_TIME();
  responseType: 'en' = 'en';
  parentId: string;
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<AskContinueRepeatMessage>
  ) {
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    Object.assign(this, value);
  }

  async next(userMessage: UserMessage): Promise<MessageEvent[]> {
    const wantsContinue = await wantsChallengeContinue(userMessage.content);
    return [
      new ContinueAnswerMessage(() => this.getConversation(), {
        role: wantsContinue ? 'continue_yes' : 'continue_no',
        parentId: this.parentId
      })
    ];
  }

  get messages(): ViewMessage[] {
    return [
      new AssistantMessage(this, this.getConversation, {
        content: 'Would you like to keep going?',
      }),
    ];
  }
}

type ContinueAnswerRole = 'continue_yes' | 'continue_no';

class ContinueAnswerMessage {
  role: ContinueAnswerRole = 'continue_no';
  playCount = 0;
  time = MONOTONIC_TIME();
  parentId: string;
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<ContinueAnswerMessage>
  ) {
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    Object.assign(this, value);
  }

  get messages(): ViewMessage[] {
    if (this.role === 'continue_yes') {
      return [];
    }
    return [
      new AssistantMessage(this, this.getConversation, {
        content: `No problem! Let's move on.`,
      }),
    ];
  }
}

type ChallengeFailRole = 'meaning_fail' | 'repeat_fail';
type ChallengeSuccessRole = 'repeat_success' | 'meaning_success' | 'translate_success';
type ChallengeOutcomeRole = ChallengeFailRole | ChallengeSuccessRole;

const SUCCESS_ROLE_RANK: Record<ChallengeSuccessRole, number> = {
  repeat_success: 1,
  meaning_success: 2,
  translate_success: 3,
};

class ChallengeOutcomeMessage {
  role: ChallengeOutcomeRole = 'repeat_fail';
  index: number;
  userMessageId: string;
  loaded?: Promise<unknown>;
  translationLoaded: boolean = false;
  loadError?: boolean;
  playCount = 0;
  time = MONOTONIC_TIME();
  parentId: string;
  pronunciationScore = 0;
  feedback?: string;
  attempt = 1;
  get id(): string {
    return this.time;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<ChallengeOutcomeMessage>
  ) {
    this.role = value.role ?? this.role;
    checkForKey('index', value);
    this.index = value.index!;
    checkForKey('userMessageId', value);
    this.userMessageId = value.userMessageId!;
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    Object.assign(this, value);
    if (this.role === 'meaning_fail') {
      this.loaded = this.loadTranslation();
    }
    if (this.role === 'repeat_fail') {
      this.loaded = this.loadPronunciation();
    }
  }

  get messages(): ViewMessage[] {
    if (this.loadError) {
      const errorText = this.role === 'meaning_fail'
        ? 'Having trouble loading the correct translation.'
        : 'Having trouble loading pronunciation feedback.';
      return [
        new AssistantMessage(this, this.getConversation, {
          content: errorText,
        }),
      ];
    }
    if (this.role === 'meaning_fail') {
      const expression = this.getConversation().getExpression(this.index);
      const reason = this.feedback ? [
        new AssistantMessage(this, this.getConversation, {
          content: this.feedback,
          silent: true,
        }),
      ] : [];
      return [
        new AssistantMessage(this, this.getConversation, { content: `The correct meaning is:` })
      ].concat(
        expression.native ? new AssistantMessage(this, this.getConversation, { content: expression.native }) : []
      ).concat(reason);
    }
    if (this.role === 'meaning_success') {
      return [
        new TapbackMessage({ type: 'heart', userMessageId: this.userMessageId }),
      ];
    }
    const scorePercent = Math.round(this.pronunciationScore * 100);
    const tapback: Tapback['type'] = scorePercent >= 100 ? 'heart' : 'thumbsup';
    return [
      ...(this.role !== 'repeat_fail' ? [
        new TapbackMessage({ type: tapback, userMessageId: this.userMessageId })
      ] : []),
      ...(scorePercent < (PRONUNCIATION_THRESHOLD * 100) ? [
        new TapbackMessage({
          type: 'text',
          text: `${scorePercent}%`,
          silent: true,
          userMessageId: this.userMessageId,
        }),
      ] : []),
      ...(this.feedback && scorePercent < 100 ? [
        new AssistantMessage(this, this.getConversation, {
          content: `Pronunciation tip: ${this.feedback}`,
          silent: true,
        }),
      ] : []),
    ];
  }

  private async loadTranslation() {
    const conversation = this.getConversation();
    await conversation.ensureTranslation(this.index);
    this.translationLoaded = true;
    conversation.notifyStateChange();
  }

  async loadPronunciation() {
    const conversation = this.getConversation();
    await conversation.ensurePronunciation(this.index);
    this.getConversation().notifyStateChange();
  }
}

type ChallengeRole = 'initial_repeat' | 'repeat' | 'meaning' | 'translate';

class ChallengeMessage {
  role: ChallengeRole = 'repeat';
  playCount = 0;
  time = MONOTONIC_TIME();
  index: number;
  attempt = 1;
  loaded?: Promise<void>;
  loadError?: boolean;
  parentId: string;
  get id(): string {
    return this.time;
  }

  get responseType(): LanguageCode {
    return this.role === 'meaning' ? this.getConversation().nativeLanguageCode : this.getConversation().languageCode;
  }

  constructor(
    public getConversation: ConversationGetter,
    value: Partial<ChallengeMessage>
  ) {
    this.role = value.role ?? this.role;
    checkForKey('index', value);
    this.index = value.index!;
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    const { id, responseType, ...rest } = value;
    Object.assign(this, rest);

    if (this.role === 'initial_repeat' && this.loaded == null) {
      const conversation = this.getConversation();
      const expression = conversation.getMutableExpression(this.index);
      const ensureMetadata = async () => {
        if (!expression) {
          return;
        }
        await Promise.all([
          expression.native ? Promise.resolve() : conversation.ensureTranslation(this.index),
          expression.breakdown ? Promise.resolve() : conversation.ensureBreakdown(this.index),
          expression.pronunciation ? Promise.resolve() : conversation.ensurePronunciation(this.index),
        ]);
      };
      this.loaded = ensureMetadata();
    }

    if (this.role === 'translate') {
      const conversation = this.getConversation();
      if (!conversation.getExpression(this.index).native) {
        this.loaded = conversation.ensureTranslation(this.index);
      }
    }
  }

  get messages(): ViewMessage[] {
    const conversation = this.getConversation();
    const expression = conversation.getExpression(this.index);
    const results: AssistantMessage[] = [];
    if (this.loadError) {
      const errorText = this.role === 'translate'
        ? 'Having trouble loading the translation.'
        : 'Having trouble loading lesson details.';
      results.push(new AssistantMessage(this, this.getConversation, {
        content: errorText,
      }));
    }

    if (this.role === 'meaning') {
      return [new AssistantMessage(this, this.getConversation, {
        content: `What does it mean?`,
      })];
    }

    if (this.role === 'initial_repeat' || this.role === 'repeat') {
      results.push(new AssistantMessage(this, this.getConversation, {
        content: `Please repeat the following:`,
        silent: true,
      }));
    }

    if ((this.role === 'initial_repeat' || this.role === 'translate') && this.attempt === 1) {
      if (this.role === 'translate') {
        results.push(new AssistantMessage(this, this.getConversation, {
          content: `Please translate the following:`,
          silent: true,
        }));
      }

      if (expression.native) {
        results.push(new AssistantMessage(this, this.getConversation, {
          content: expression.native,
          languageCode: conversation.nativeLanguageCode,
          links: [{
            name: 'Details',
            type: 'overview',
            expression: expression.value,
            index: this.index,
          }],
        }));
      }
    }

    if (this.role === 'initial_repeat' && this.attempt === 1) {
      if (expression.pronunciation) {
        results.push(new AssistantMessage(this, this.getConversation, {
          content: `Pronunciation tip: ${expression.pronunciation}`,
          silent: true,
        }));
      }
    }

    // Don't show the translated message for the first translate challenge.
    if (this.role === 'translate' && this.attempt === 1) {
      return results;
    }

    results.push(new AssistantMessage(this, this.getConversation, {
      content: expression.value,
      languageCode: conversation.languageCode,
      rate: this.attempt >= 4 ? 0.5 : 1,
      links: [{
        name: 'Details',
        type: 'overview',
        expression: expression.value,
        index: this.index,
      }],
      removeRestoreButton: true,
      breakdownButton: true,
    }));

    return results;
  }

  async next(userMessage: UserMessage): Promise<MessageEvent[]> {
    const conversation = this.getConversation();
    const content = conversation.getExpression(this.index).value;

    if (this.role === 'meaning') {
      const results: MessageEvent[] = [];

      const systemTranslate: Message = {
        role: 'system',
        content: `Grade the translation.`
          + ` Answer one word 'EXACT', 'CLOSE', or 'WRONG' with explanation when 'WRONG'.`,
      };

      const rateTranslation = `Source: "${content}" Translation: "${userMessage.content}" Answer: '`;
      const ratingRes = await generate([systemTranslate, { role: 'user', content: rateTranslation }]);
      const rating = ratingRes.content.trim();
      const ratingUpper = rating.toUpperCase();
      const userWords = getWords(userMessage.content, conversation.nativeLanguageCode).join(' ');
      const words = getWords(content, conversation.nativeLanguageCode).join(' ');
      const wrong = ratingUpper.startsWith('WRONG') && !userWords.includes(words);
      const success = !wrong;
      const meaningFailReason = wrong
        ? rating.replace(/^WRONG\b[:\-\s]*/i, '').trim()
        : undefined;

      const role: ChallengeOutcomeRole = success ? 'meaning_success' : 'meaning_fail';
      const event = new ChallengeOutcomeMessage(() => conversation, {
        role,
        index: this.index,
        userMessageId: userMessage.id,
        feedback: meaningFailReason,
        parentId: this.id,
      });
      return results.concat(event);
    }

    const evaluation = await evaluatePronunciation(
      conversation,
      content,
      userMessage.content,
    );
    const role: ChallengeOutcomeRole = evaluation.passed
      ? (this.role === 'translate' && this.attempt === 1
        ? 'translate_success'
        : 'repeat_success')
      : 'repeat_fail';
    const event = new ChallengeOutcomeMessage(() => conversation, {
      role,
      index: this.index,
      userMessageId: userMessage.id,
      pronunciationScore: evaluation.score,
      feedback: evaluation.feedback,
      attempt: this.attempt,
      parentId: this.id,
    });
    return [event];
  }
}

function pronunciationScore(target: string, spoken: string, languageCode: LanguageCode) {
  const normalize = (value: string) => getWords(value, languageCode).join('').split('');
  const targetChars = normalize(target);
  const spokenChars = normalize(spoken);
  if (!targetChars.length) {
    return 0;
  }
  const remaining = [...spokenChars];
  let matches = 0;
  for (const char of targetChars) {
    const index = remaining.indexOf(char);
    if (index !== -1) {
      matches += 1;
      remaining.splice(index, 1);
    }
  }
  return matches / targetChars.length;
}

async function pronunciationAssessment(
  target: string,
  attempt: string,
  nativeLanguageCode: LanguageCode,
  languageCode: LanguageCode,
) {
  const nativeLanguage = getLanguageName(nativeLanguageCode);
  let errorFeedback = '';
  let rawFeedback = '';
  const calcScore = pronunciationScore(target, attempt, languageCode);
  const prompt: Message[] = [
    {
      role: "user",
      content:
        `Output target "${target}" and attempt "${attempt}", as ${nativeLanguage} syllables, ` +
        'then feedback, in laymans terms and in less than 18 words, on how to make attempt sound like target. ' +
        'Output only format text.' +
        `Format: "<target ${nativeLanguage} syllables>|<attempt ${nativeLanguage} syllables>|<feedback>"`,
    },
  ];
  try {
    const result = await generate(prompt);
    rawFeedback = result.content.trim();
    const [targetSyllables, attemptSyllables, description] = result.content.split('|').map((part) => part.trim());
    const score = diceCoefficient(targetSyllables, attemptSyllables);
    console.log("Pronunciation assessment response:", { targetSyllables, attemptSyllables, description, score, rawFeedback });
    if (Number.isFinite(score) && description) {
      return {
        score: score,
        feedback: `${targetSyllables} vs ${attemptSyllables} - ${description}`,
      };
    }
  } catch (error) {
    writeError("Failed to generate pronunciation feedback", error);
    errorFeedback = 'Having trouble getting pronunciation feedback.';
  }
  return {
    score: calcScore,
    feedback: errorFeedback || rawFeedback,
  };
}

function wantsPhraseConfirmation(input: string) {
  return /^(yes|yep|yeah|yup|ok|okay|sure|sounds good|looks good|great|perfect|correct)\b/i.test(input.trim());
}

async function wantsChallengeContinue(input: string) {
  const prompt: Message[] = [
    {
      role: "system",
      content:
        "Decide if the learner wants to continue the current speaking challenge. Respond YES or NO.",
    },
    {
      role: "user",
      content: input,
    },
  ];
  try {
    const response = await generate(prompt);
    console.log("Continue intent response:", response.content);
    return response.content.trim().toUpperCase().startsWith("YES");
  } catch (error) {
    writeError("Failed to determine continue intent", error);
    return false;
  }
}

function buildIntentPrompt(
  conversation: Conversation,
  request?: string,
  historyText = '',
): Message[] {
  const language = getLanguageName(conversation.languageCode);
  const feedbackText = request ? `User:\n${request}.\n` : '';
  return [
    {
      role: 'system',
      content: `Come up with 4 short ${language} phrases, unambiguous when spoken aloud, learners can practice. Output each phrase on its own line with no numbering, labels, or translations. Keep every phrase at most 9 words.`,
    },
    {
      role: 'user',
      content: `${historyText}${feedbackText}:\nPhrases:\n`,
    },
  ];
}

async function expressionsFromDialog(
  conversation: Conversation,
  content: string,
): Promise<Expression[]> {
  const trim = (line: string) => line.trim();
  const empty = (line: string) => line.length > 0;
  const existingValues = conversation.expressions.map((expression) => expression.value);
  const lines = content
    .split('\n')
    .map(trim)
    .filter(empty)
    .filter((line) => !existingValues.includes(line));
  if (lines.length === 0) {
    return [];
  }
  const dialog = lines.join('\n');

  let translatedDialog = '';
  try {
    translatedDialog = await translate(dialog, conversation.nativeLanguageCode);
  } catch (error) {
    writeError('Failed to translate expressions', error);
    translatedDialog = dialog;
  }

  let translatedLines = dialog.split('\n').map(trim).filter(empty);
  const nativeLines = translatedDialog.split('\n').map(trim).filter(empty);

  if (nativeLines.length !== translatedLines.length) {
    writeError('Expressions translations mismatch', {
      translatedCount: translatedLines.length,
      nativeCount: nativeLines.length,
    });
    translatedLines = [];
  }

  return translatedLines.map((line, index) => ({
    value: line,
    native: nativeLines[index],
    repeatFailCount: 0,
    translateFailCount: 0,
    pronunciationScores: [],
  }));
}

async function generateBreakdownAnswer(
  conversation: Conversation,
  expressionIndex: number,
  question: string
): Promise<string> {
  const expression = conversation.getMutableExpression(expressionIndex);
  if (!expression) {
    return "I can't find that expression right now.";
  }
  await Promise.all([
    expression.pronunciation ? Promise.resolve() : conversation.ensurePronunciation(expressionIndex),
    expression.breakdown ? Promise.resolve() : conversation.ensureBreakdown(expressionIndex),
  ]);
  const nativeLanguage = getLanguageName(conversation.nativeLanguageCode);
  const expressionSummary = [
    `Expression: ${expression.value}`,
    `Translation: ${expression.native ?? ''}`,
    `Pronunciation: ${expression.pronunciation ?? ''}`,
    `Breakdown: ${expression.breakdown ?? ''}`,
  ].join('\n');
  const prompt: Message[] = [
    {
      role: "system",
      content:
        `You are a language tutor. Answer the learner's question in ${nativeLanguage}. ` +
        "Be concise (2 sentences max) and focus on the expression details provided.",
    },
    {
      role: "user",
      content: `${expressionSummary}\n\nQuestion: ${question}`,
    },
  ];
  const response = await generate(prompt);
  return response.content.trim();
}

type ConversationScene = {
  setting: string;
  goal: string;
};

const CONVERSATION_SCENES: ConversationScene[] = [
  { setting: 'a cafe', goal: 'order a drink politely' },
  { setting: 'a train station', goal: 'ask for the right platform' },
  { setting: 'a new friend meetup', goal: 'share basic personal info' },
  { setting: 'a restaurant', goal: 'ask for a recommendation' },
  { setting: 'a street market', goal: 'ask the price and negotiate' },
];

function getConversationScene(conversation: Conversation): ConversationScene {
  const turns = conversation._messages.filter((m) => m.role === 'conversation').length;
  return CONVERSATION_SCENES[turns % CONVERSATION_SCENES.length];
}

function conversationLevelHint(level: number): string {
  if (level <= 0) {
    return 'Use a simple yes/no or A/B question. Keep it very short.';
  }
  if (level === 1) {
    return 'Use a short open question. One clause, simple words.';
  }
  return 'Ask for a reason or small detail. Still one short sentence.';
}

function getConversationAnchors(conversation: Conversation): string[] {
  const reviewable = conversation.reviewableExpressions
    .slice(0, 3)
    .map((index) => conversation.getExpression(index).value)
    .filter((value): value is string => !!value);
  if (reviewable.length > 0) {
    return reviewable;
  }
  return conversation.expressions
    .slice(-3)
    .map((expression) => expression.value)
    .filter((value) => value.trim().length > 0);
}

async function generateConversationQuestion(conversation: Conversation, input: string, plan?: string) {
  const language = getLanguageName(conversation.languageCode);
  const scene = getConversationScene(conversation);
  const lastReply = input.trim();
  const level = conversation.conversationSkill;
  const anchors = getConversationAnchors(conversation);
  const goalFromPlan = plan?.match(/Goal:\s*(.+)/i)?.[1]?.trim();
  const hasPlan = typeof plan === 'string' && plan.trim().length > 0;
  const prompt: Message[] = [
    {
      role: "system",
      content:
        `You are the ${scene.setting} person. ` +
        `Speak only in ${language}. ` +
        `If there is a last reply, first acknowledge it with ONE short sentence, ` +
        `then ask ONE short question. ` +
        `If there is no last reply, just ask ONE short question. ` +
        `No translations.`,
    },
    {
      role: "user",
      content:
        `${hasPlan ? `Plan (do NOT reveal options): ${plan}. ` : `Scene: ${scene.setting}. `}` +
        `${hasPlan ? `Goal: ${goalFromPlan ?? scene.goal}. ` : `Goal: ${scene.goal}. `}` +
        `Learner level: ${level}. ${conversationLevelHint(level)} ` +
        `${anchors.length ? `Use at least one of these known phrases if it fits: ${anchors.join(' | ')}. ` : ''}` +
        `Last reply: ${lastReply ? `"${lastReply}"` : 'none'}. ` +
        `Advance the goal and check their level.`,
    },
  ];
  try {
    const response = await generate(prompt);
    const question = response.content.trim().split('\n')[0];
    if (question) {
      return question;
    }
  } catch (error) {
    writeError("Failed to generate conversation question", error);
  }
  const fallback = await translate('Tell me more.', conversation.languageCode);
  return fallback.trim() || 'Tell me more.';
}

function wantsReview(input: string) {
  return /(^|\b)(review|revise|revision|refresh)(\b|$)/i.test(input.trim());
}

function wantsConversation(input: string) {
  return /(^|\b)(conversation|chat|talk|talking|speak|speaking|questions|free\s*talk)(\b|$)/i
    .test(input.trim());
}

function wantsPhrases(input: string) {
  return /(^|\b)(phrases|phrase|learn|practice|lesson|expressions)(\b|$)/i
    .test(input.trim());
}

function wantsVariations(input: string) {
  return /(^|\b)(variation|variations|remix|rephrase|alternate|alternates)(\b|$)/i
    .test(input.trim());
}

function getVariationBaseExpressions(conversation: Conversation, request: string): Expression[] {
  const tokens = (request.toLowerCase().match(/[a-z0-9]+/g) ?? [])
    .filter((token) => token.length > 2);
  if (tokens.length === 0) {
    return [];
  }
  const scoreByIndex = new Map<number, number>();
  for (const entry of conversation.phraseRequests) {
    const score = tokens.reduce((total, token) => total + (entry.request.toLowerCase().includes(token) ? 1 : 0), 0);
    if (score > 0) {
      for (const index of entry.indices) {
        scoreByIndex.set(index, Math.max(scoreByIndex.get(index) ?? 0, score));
      }
    }
  }
  for (const [index, expression] of conversation.expressions.entries()) {
    const haystack = `${expression.native ?? ''} ${expression.value}`.toLowerCase();
    const score = tokens.reduce((total, token) => total + (haystack.includes(token) ? 1 : 0), 0);
    if (score > 0) {
      scoreByIndex.set(index, Math.max(scoreByIndex.get(index) ?? 0, score));
    }
  }
  return Array.from(scoreByIndex.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([index]) => conversation.getExpression(index));
}

class OutcomeMessage {
  playCount = 0;
  role: 'failure' | 'success' = 'failure';
  time = MONOTONIC_TIME();
  evaluation?: string;
  parentId: string;
  get id(): string {
    return this.time;
  }

  constructor(public getConversation: () => Conversation, value: Partial<OutcomeMessage>) {
    checkForKey('parentId', value);
    this.parentId = value.parentId!;
    Object.assign(this, value);
  }

  get messages(): ViewMessage[] {
    if (this.role === 'failure') {
      const failure: 'failure' = 'failure';
      const links: Link[] | undefined = this.evaluation ? [{
        name: 'Failure reason',
        type: failure,
        reason: {
          name: 'Explanation',
          value: this.evaluation
        }
      }] : undefined;
      return [
        new AssistantMessage(this, this.getConversation, {
          content: '😭 Failed 😭',
          sound: 'wrong',
          size: '5',
          links,
        })
      ];
    }
    return [
      new AssistantMessage(this, this.getConversation, {
        content: 'Great job!',
      }),
      new AssistantMessage(this, this.getConversation, {
        content: '🏆🏆🏆',
        sound: 'right',
        size: '9',
      })
    ];
  }
}


async function playEvent(event: MessageEvent, forceUpdate: () => void): Promise<void> {
  if (event.role === 'user') {
    return event.play();
  }
  while (event.playCount < event.messages.length) {
    const message = event.messages[event.playCount];
    if ('loaded' in message) {
      try {
        await message.loaded;
        forceUpdate();
      } catch (error) {
        writeError('Failed to resolve message promise', error);
        if ('loadError' in event) {
          event.loadError = true;
        }
        forceUpdate();
        break;
      }
    }
    event.playCount += 1;
    await wait(0);
    forceUpdate();
    await message?.play();
  }
}

function renderEvent(event: MessageEvent): PromptMessage[] {
  const { messages } = event;
  const result = event.role === 'user' ? messages : messages.slice(0, event.playCount);
  return result.filter((m): m is PromptMessage => m.role !== 'tapback');
}

type BreakdownPromptMessage = BreakdownMessage & { role: 'breakdown_prompt' };
type BreakdownAnswerMessage = BreakdownMessage & { role: 'breakdown_answer'; answer: string };

type ResponseMessage =
  | AskNextMessage
  | PhrasesMessage
  | AskContinueRepeatMessage
  | ConversationMessage
  | BreakdownPromptMessage
  | BreakdownMessage
  | LessonMessage
  | ChallengeMessage;
type ResultMessage =
  | ChallengeOutcomeMessage
  | BreakdownAnswerMessage
  | OutcomeMessage
  | ContinueAnswerMessage;
type TutorMessage = ResponseMessage | ResultMessage;
export type MessageEvent = UserMessage | TutorMessage;
export type MessageEventRole = MessageEvent['role'];

type MessageEventByRole<R extends MessageEventRole> =
  Extract<MessageEvent, { role: R }>;

export type ConversationSnapshot = Omit<ConversationState, 'revision' | 'shock'>;


function cloneMessageEvent(event: MessageEvent): MessageEvent {
  const cloned: MessageEvent = JSON.parse(JSON.stringify(event, (key, value) => {
    if (key === 'loaded') {
      return undefined;
    }
    if (key === 'event') {
      return undefined;
    }
    if (key === 'failureHelpers') {
      return undefined;
    }
    if (key === 'breakdownText') {
      return undefined;
    }
    return value;
  }));
  return cloned;
}


const deserialize = (
  conversation: Conversation,
  onError: (value: unknown) => void,
) => (m: MessageEvent): MessageEvent | null => {
  switch (m.role) {
    case 'success':
    case 'failure':
      return new OutcomeMessage(() => conversation, m);
    case 'user':
      return new UserMessage(() => conversation, m);
    // case 'welcome':
    //   return new WelcomeMessage(() => conversation, m);
    case 'meaning_fail':
    case 'repeat_fail':
    case 'meaning_success':
    case 'repeat_success':
    case 'translate_success':
      return new ChallengeOutcomeMessage(() => conversation, m);
    case 'ask_next':
      return new AskNextMessage(() => conversation, m);
    case 'breakdown_prompt':
    case 'breakdown_answer':
      return new BreakdownMessage(() => conversation, m);
    case 'ask_continue_repeat':
      return new AskContinueRepeatMessage(() => conversation, m);
    case 'phrases':
      return new PhrasesMessage(() => conversation, m);
    case 'conversation':
      return new ConversationMessage(() => conversation, m);
    case 'lesson':
      return new LessonMessage(() => conversation, m);
    case 'repeat':
    case 'initial_repeat':
    case 'meaning':
    case 'translate':
      return new ChallengeMessage(() => conversation, m);
    case 'continue_no':
    case 'continue_yes':
      return new ContinueAnswerMessage(() => conversation, m);
    default:
      onError(m);
      return null;
  }
}


const getPreviousEvent = (messages: MessageEvent[]) => {
  const roles: MessageEvent['role'][] = [
    'ask_next',
    'ask_continue_repeat',
    'phrases',
    'conversation',
    'breakdown_prompt',
    'breakdown_answer',
    'initial_repeat',
    'repeat',
    'meaning',
    'translate',
  ];
  return messages.findLast((a): a is ResponseMessage => roles.includes(a.role));
}


const getReviewStats = (expressions: Expression[], removedExpressions: number[]) => {
  const now = Date.now();
  const soonWindowMs = 6 * HOUR_MS;
  const candidates: { index: number; dueAt: number }[] = [];
  const schedule: { index: number; dueAt: number; stage: number; intervalMs: number; ready: boolean }[] = [];
  const stageCounts = Array(REVIEW_INTERVALS_MS.length).fill(0);
  let readyCount = 0;
  let soonCount = 0;
  let laterCount = 0;
  let nextDueAt: number | null = null;
  let oldestDueAt: number | null = null;
  for (let index = 0; index < expressions.length; index += 1) {
    if (removedExpressions.includes(index)) {
      continue;
    }
    const expression = expressions[index];
    const baseStage = Math.min(expression.reviewStage ?? 0, REVIEW_INTERVALS_MS.length - 1);
    if (baseStage < 0) {
      continue;
    }
    const lastSuccessTime = expression.lastSuccessTime;
    if (!lastSuccessTime) {
      continue;
    }
    const lastTime = Date.parse(lastSuccessTime);
    if (Number.isNaN(lastTime)) {
      continue;
    }
    const intervalMs = REVIEW_INTERVALS_MS[baseStage];
    const dueAt = lastTime + intervalMs;
    stageCounts[baseStage] += 1;
    const ready = now >= dueAt;
    schedule.push({ index, dueAt, stage: baseStage, intervalMs, ready });
    if (ready) {
      candidates.push({ index, dueAt });
      readyCount += 1;
      if (oldestDueAt == null || dueAt < oldestDueAt) {
        oldestDueAt = dueAt;
      }
    } else if (dueAt <= now + soonWindowMs) {
      soonCount += 1;
    } else {
      laterCount += 1;
    }
    if (nextDueAt == null || dueAt < nextDueAt) {
      nextDueAt = dueAt;
    }
  }
  return {
    candidates,
    schedule: schedule.sort((a, b) => a.dueAt - b.dueAt),
    readyCount,
    soonCount,
    laterCount,
    nextDueAt,
    oldestDueAt,
    stageCounts,
    total: stageCounts.reduce((a, b) => a + b, 0),
  };
};

class Conversation {
  version = SNAPSHOT_VERSION;
  shock = false;
  loadFailed = false;
  conversationSkill = 0;
  removedExpressions: number[] = [];
  languageCode: LanguageCode;
  _messages: MessageEvent[] = [];
  expressions: Expression[] = [];
  phraseRequests: PhraseRequest[] = [];
  activityEvents: ActivityEvent[] = [];
  pushEnabledDevices: string[] = [];
  private activeStart: string | null = null;
  private revision = 0;
  private snapshotState: ConversationState = {
    revision: 0,
    version: 0,
    shock: false,
    expressions: [],
    phraseRequests: [],
    activityEvents: [],
    messages: [],
    removedExpressions: [],
  };
  private listeners = new Set<() => void>();
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private playingMessages = false;
  private syncInitialized = false;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSyncedRevision = 0;
  private suppressSync = false;
  private lastReminderKey: string | null = null;
  private lastSyncAt: string | null = null;
  private lastSyncError: string | null = null;
  private hydrateErrorPersistent = false;

  constructor(
    public language: Language,
    public nativeLanguageCode: LanguageCode
  ) {
    this.languageCode = this.language.code as LanguageCode;
    this.restore();
    this.refreshSnapshot();

    if (this._messages.length === 0) {
      this.addMessage(this.entryMessage());
    } else {
      const previous = this.previousEvent;
      const reviewReady = this.reviewableExpressions.length > 0;
      if (reviewReady && previous?.role === 'ask_next' && (previous.reviewCount ?? 0) === 0) {
        void this.addMessage(new AskNextMessage(() => this, {}));
      }
    }
  }

  entryMessage(): MessageEvent {
    return new AskNextMessage(() => this, {});
  }

  private refreshSnapshot(): void {
    this.snapshotState = {
      revision: this.revision,
      version: this.version,
      shock: this.shock,
      expressions: this.expressions,
      phraseRequests: this.phraseRequests,
      activityEvents: this.activityEvents,
      messages: this._messages,
      removedExpressions: this.removedExpressions,
      lastSyncAt: this.lastSyncAt,
      lastSyncError: this.lastSyncError,
      pushEnabledDevices: this.pushEnabledDevices,
    };
  }

  getState(): ConversationState {
    return this.snapshotState;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emitState(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }

  private schedulePersist(): void {
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      this.persist();
    }, 500);
  }

  notifyStateChange(): void {
    this.revision += 1;
    this.refreshSnapshot();
    this.emitState();
    this.schedulePersist();
    this.scheduleSync();
  }

  private restore() {
    const stored = localStorage.getItem(this.key);
    if (!stored) {
      return;
    }
    try {
      const raw = JSON.parse(stored);
      if (raw && typeof raw === 'object') {
        this.lastSyncAt = typeof raw.lastSyncAt === 'string' ? raw.lastSyncAt : null;
        this.lastSyncError = typeof raw.lastSyncError === 'string' ? raw.lastSyncError : this.lastSyncError;
        this.pushEnabledDevices = Array.isArray(raw.pushEnabledDevices) ? raw.pushEnabledDevices.filter((id: string) => typeof id === 'string') : [];
      }
      this.loadSnapshot(raw);
    } catch (error) {
      this.loadFailed = true;
      this.setHydrationError('Failed to parse local conversation data');
      writeError('Error parsing conversation data', error);
    }
  }

  private get snapshot(): ConversationSnapshot {
    return {
      version: this.version,
      expressions: this.expressions,
      phraseRequests: this.phraseRequests,
      activityEvents: this.activityEvents,
      messages: this._messages.map(cloneMessageEvent),
      removedExpressions: this.removedExpressions,
      lastSyncAt: this.lastSyncAt,
      lastSyncError: this.lastSyncError,
      pushEnabledDevices: this.pushEnabledDevices,
    };
  }

  private get syncSnapshot(): ConversationSnapshot {
    return {
      version: this.version,
      expressions: this.expressions,
      phraseRequests: this.phraseRequests,
      activityEvents: this.activityEvents,
      messages: [],
      removedExpressions: this.removedExpressions,
      pushEnabledDevices: this.pushEnabledDevices,
    };
  }

  private applyQueueSilently(): void {
    for (const event of this._messages) {
      event.playCount = event.messages.length;
    }
    this.notifyStateChange();
  }

  loadSnapshot(raw: ConversationSnapshot): void {
    if (raw && typeof raw === 'object') {
      this.lastSyncAt = typeof raw.lastSyncAt === 'string' ? raw.lastSyncAt : this.lastSyncAt;
      if (!this.hydrateErrorPersistent) {
        this.lastSyncError = typeof raw.lastSyncError === 'string' ? raw.lastSyncError : this.lastSyncError;
      }
      if (Array.isArray(raw.pushEnabledDevices)) {
        this.pushEnabledDevices = raw.pushEnabledDevices.filter((id) => typeof id === 'string');
      }
    }
    const migrated = migrateSnapshot(raw);
    this.expressions = migrated.expressions ?? [];
    this.phraseRequests = migrated.phraseRequests ?? [];
    this.activityEvents = migrated.activityEvents ?? [];
    this.removedExpressions = migrated.removedExpressions ?? [];
    const restored: MessageEvent[] = [];
    for (const message of migrated.messages) {
      const next = deserialize(this, (value) => {
        this.loadFailed = true;
        this.setHydrationError('Failed to hydrate conversation message');
        writeError('unable to deserialize', value);
      })(message);
      if (next) {
        restored.push(next);
      }
    }
    this._messages = restored;
    this.messageLookup = {};
    this.applyQueueSilently();
  }

  mergeSnapshot(raw: ConversationSnapshot): void {
    const merged = mergeSnapshots(this.snapshot, migrateSnapshot(raw));
    this.loadSnapshot(merged);
  }

  getSnapshot(): ConversationSnapshot {
    return this.snapshot;
  }

  get key(): string {
    return `StateStore-Conversation-${this.language.code}-${this.nativeLanguageCode}`;
  }

  get messages(): PromptMessage[] {
    // console.log('rerender messages', this._messages.slice(0).reverse().map((m) => ({
    //   role: m.role,
    //   playCount: m.playCount,
    //   messages: m.messages.map((msg) => (msg as { content?: string }).content),
    // })));
    return this._messages.slice(0).flatMap(renderEvent);
  }

  expressionHas(role: ChallengeSuccessRole, index: number, messages = this._messages): boolean {
    if (messages.some((message) => message.role === role && message.index === index)) {
      return true;
    }
    const expression = this.getMutableExpression(index);
    if (!expression?.lastSuccessRole) {
      return false;
    }
    return SUCCESS_ROLE_RANK[expression.lastSuccessRole] >= SUCCESS_ROLE_RANK[role];
  }

  getExpression(index: number): Expression {
    return this.getMutableExpression(index) ?? FAILED_TO_LOAD_EXPRESSION;
  }

  getMutableExpression(index: number): Expression | undefined {
    return this.expressions[index];
  }

  get learnedWords(): string[] {
    const learnedExpressions = [
      ...new Set(
        this.expressions
          .filter((expression) => expression.lastSuccessRole != null)
          .map((expression) => expression.value)
      ),
    ];
    return getWords(learnedExpressions.join(' '), this.languageCode);
  }

  get wordCount() {
    const words = this.learnedWords;
    return words.filter((w, i) => words.indexOf(w) === i).length;
  }

  get currentChallenge(): ChallengeMessage | undefined {
    return this._messages.findLast(isChallengeMessage);
  }

  get lessonActive(): boolean {
    const previous = this.previousEvent;
    const entryRoles: MessageEvent['role'][] = [
      'ask_next',
      'phrases',
      'conversation',
    ];
    if (previous && entryRoles.includes(previous.role)) {
      return false;
    }
    return this.lessonProgress.total > 0;
  }

  get started(): boolean {
    return this._messages.some((message) => message.role === 'user');
  }

  private messageLookup: { [key: string]: number | null } = {};
  getMessageById<R extends MessageEventRole>(id: string, role: R): MessageEventByRole<R> | null;
  getMessageById(id: string): MessageEvent | null;
  getMessageById(id: string, role?: MessageEventRole) {
    if (!this.messageLookup[id]) {
      this.messageLookup[id] = this._messages.findIndex((message) => message.id === id) ?? null;
    }
  
    const message = this._messages[this.messageLookup[id]];
    if (role && message?.role !== role) {
      writeError(`Message with id ${id} is not of role '${role}', found '${message?.role}'`);
      return null;
    }
  
    return message ?? null;
  }

  get lessonProgress(): { current: number; total: number } {
    const { parentId } = this.currentChallenge ?? {};
    if (!parentId) {
      return { current: 0, total: 0 };
    }
    const indices = getLessonIndices(this, parentId);
    const completedIndices = getLessonCompletedIndices(this, parentId);
    const remainingIndices = indices.filter((index) => !completedIndices.includes(index));
    const sessionStart = this._messages.findLastIndex((m) => m.role === 'lesson');

    // const parentIndex = this._messages.findIndex(m => m.id === parentId)
    // console.log({ recent: this._messages[sessionStart], parent: this._messages[parentIndex], messages: this._messages.slice(-4) });

    if ((parentId !== this._messages[sessionStart]?.id) || sessionStart === -1) {
      return { current: 0, total: 0 };
    }

    const completedTotal = indices.length - remainingIndices.length;
    const lessonMessage = this._messages[sessionStart] as LessonMessage;

    const distancesValues: { [key: string]: number } = {
      'translate_success': 1,
      'meaning_success': 1,
      'repeat_success': 2,
    };

    const hasRole = (role: ChallengeSuccessRole, index: number, messages: MessageEvent[]) =>
      messages.some((message) => message.role === role && message.index === index);

    const nextRoleFor = (index: number, messages: MessageEvent[], fallback: ChallengeSuccessRole | null): ChallengeSuccessRole | null => (
      hasRole('translate_success', index, messages) || fallback === 'translate_success'
        ? 'translate_success'
        : hasRole('meaning_success', index, messages) || fallback === 'meaning_success'
          ? 'meaning_success'
          : hasRole('repeat_success', index, messages) || fallback === 'repeat_success'
            ? 'repeat_success'
            : fallback
    );

    const latestSuccess = (messages: MessageEvent[]) => (i: number) => {
      const fallback = lessonMessage.startingRoles[indices.indexOf(i)] ?? null;
      return nextRoleFor(i, messages, fallback);
    };

    const total = lessonMessage.startingRoles
      .map(role => (role && distancesValues[role]) ?? 3)
      .reduce((a, b) => a + b, 0);

    const current = (total - indices
      .map(latestSuccess(this._messages))
      .map(role => (role && distancesValues[role]) ?? 3)
      .reduce((a, b) => a + b, 0)) + completedTotal;

    // console.log(this.currentChallenge)
    // console.log({ sessionStart, messageLength: this._messages.length, current, total, completedTotal, completedIndices, indices, messagesRoles: indices
    //   .map(latestSuccess(this._messages)), totalRole: indices
    //   .map(latestSuccess(beforeLesson)) });

    return { current, total };
  }

  addExpressions(expressions: Expression[]): number[] {
    const baseIndex = this.expressions.length;
    this.expressions.push(...expressions);
    this.notifyStateChange();
    return expressions.map((_, offset) => baseIndex + offset);
  }

  addPhraseRequest(request: string, indices: number[]): void {
    this.phraseRequests.push({ request, indices, time: MONOTONIC_TIME() });
  }

  startActivity(): void {
    if (this.activeStart) {
      return;
    }
    this.activeStart = MONOTONIC_TIME();
  }

  finishActivity(): void {
    if (!this.activeStart) {
      return;
    }
    const now = MONOTONIC_TIME();
    const durationMs = Date.parse(now) - Date.parse(this.activeStart);
    if (durationMs > 0) {
      this.activityEvents.push({ start: this.activeStart, durationMs });
    }
    this.activeStart = null;
  }

  get reviewableExpressions(): number[] {
    const { candidates } = getReviewStats(this.expressions, this.removedExpressions);
    return candidates
      .sort((a, b) => a.dueAt - b.dueAt)
      .map((item) => item.index)
      .filter((index) => !this.isExpressionRemoved(index));
  }

  get reviewIsReady(): boolean {
    return this.reviewableExpressions.length > 0;
  }

  get reviewSummary() {
    return getReviewStats(this.expressions, this.removedExpressions);
  }

  isExpressionRemoved(index: number): boolean {
    return this.removedExpressions.includes(index);
  }

  toggleExpressionRemoved(index: number): void {
    if (this.isExpressionRemoved(index)) {
      this.removedExpressions = this.removedExpressions.filter((value) => value !== index);
    } else {
      this.removedExpressions = this.removedExpressions.concat(index);
    }
    this.notifyStateChange();
  }

  async skipExpression(index: number): Promise<void> {
    this.removedExpressions.push(index);
    this.notifyStateChange();

    const current = this.currentChallenge;
    const source = current && getLessonIndices(this, current.parentId).includes(index)
      ? current
      : this._messages.findLast((message): message is ChallengeMessage => {
        if (!isChallengeMessage(message)) {
          return false;
        }
        return getLessonIndices(this, message.parentId).includes(index);
      });
    if (!source) {
      return;
    }
    await this.addMessage(...finishChallenge(this, source));
  }

  async ensureBreakdown(index: number): Promise<void> {
    const expression = this.getMutableExpression(index);
    if (!expression || expression.breakdown) {
      return;
    }

    try {
      const text = await breakdown(
        expression.value,
        this.nativeLanguageCode,
      );
      expression.breakdown = text;
      this.notifyStateChange();
    } catch (error) {
      writeError('Failed to load breakdown', error);
    }
  }

  async ensurePronunciation(index: number): Promise<void> {
    const expression = this.getMutableExpression(index);
    if (!expression || expression.pronunciation) {
      return;
    }

    try {
      const text = await pronounce(expression.value, this.nativeLanguageCode);
      expression.pronunciation = text;
      this.notifyStateChange();
    } catch (error) {
      writeError('Failed to load pronunciation', error);
    }
  }

  async ensureTranslation(index: number): Promise<void> {
    const expression = this.getMutableExpression(index);
    if (!expression || expression.native) {
      return;
    }
    try {
      const translated = await translate(expression.value, this.nativeLanguageCode);
      const trimmed = translated.trim();
      if (trimmed) {
        expression.native = trimmed;
      }
      this.notifyStateChange();
    } catch (error) {
      writeError('Failed to load translation', error);
    }
  }

  get nextReviewAt(): number | null {
    return getReviewStats(this.expressions, this.removedExpressions).nextDueAt;
  }

  get unplayedMessages() {
    return this._messages.filter((a) => a.playCount !== a.messages.length);
  }

  get hasUnplayed() {
    if (this.unplayedMessages.length > 0) {
      console.log('has unplayed', this.unplayedMessages.map((a) => ({
        role: a.role,
        playCount: a.playCount,
      })));
    }
    return this.unplayedMessages.length > 0;
  }

  get previousEvent() {
    return getPreviousEvent(this._messages);
  }

  get isFinale(): boolean {
    return false;
  }

  get typing(): TypingState {
    return this.hasUnplayed ? 'typing' : 'idle';
  }

  async start(): Promise<void> {
    if (this.started) {
      return;
    }
    this.notifyStateChange();
    await this.load();
  }

  initSync(): void {
    if (this.syncInitialized || typeof window === 'undefined') {
      return;
    }
    this.syncInitialized = true;
    this.sync();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void this.syncNow({ keepalive: true });
      }
    };
    const onBeforeUnload = () => {
      void this.syncNow({ keepalive: true });
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);
  }

  onSyncSessionChange(): void {
    if (!this.syncInitialized) {
      this.initSync();
      return;
    }
    this.sync();
  }

  get lastSyncTime(): string | null {
    return this.lastSyncAt;
  }

  get lastSyncErrorMessage(): string | null {
    return this.lastSyncError;
  }

  async syncNowManual(): Promise<void> {
    await this.sync();
  }

  private shouldSync(): boolean {
    return isSyncEnabled() && this.revision !== this.lastSyncedRevision;
  }

  private clearSyncTimer(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  private handleSyncError(error: unknown, context: string): void {
    if (error instanceof Error && error.message === 'sync_version_too_old') {
      window.location.reload();
      return;
    }
    if (error instanceof Error && error.message === 'sync_unauthorized') {
      clearSyncSession();
      this.lastSyncError = 'Sync session expired. Log in again.';
      this.notifyStateChange();
      return;
    }
    this.lastSyncError = error instanceof Error ? error.message : context;
    console.warn(context, error);
    writeError(context, error);
    this.notifyStateChange();
  }

  private setHydrationError(message: string): void {
    this.hydrateErrorPersistent = true;
    this.lastSyncError = message;
  }

  private scheduleSync(): void {
    if (!this.syncInitialized || this.suppressSync || !this.shouldSync()) {
      return;
    }
    this.clearSyncTimer();
    this.syncTimer = setTimeout(() => {
      this.syncTimer = null;
      void this.syncNow();
    }, 5 * 60 * 1000);
  }

  private async syncNow(options?: { keepalive?: boolean }): Promise<void> {
    if (!this.shouldSync()) {
      return;
    }
    this.clearSyncTimer();
    try {
      await this.sync(options);
    } catch (error) {
      this.handleSyncError(error, 'Failed to sync snapshot');
    }
  }

  private async sync(options?: { keepalive?: boolean }): Promise<void> {
    if (!isSyncEnabled()) {
      return;
    }
    try {
      const remoteSnapshot = await pullSnapshot(this.version);
      if (!remoteSnapshot) {
        await pushSnapshot(this.syncSnapshot, { keepalive: options?.keepalive });
        this.lastSyncedRevision = this.revision;
        this.lastSyncAt = new Date().toISOString();
        if (!this.hydrateErrorPersistent) {
          this.lastSyncError = null;
        }
        this.refreshSnapshot();
        this.emitState();
        this.persist();
        return;
      }
      this.suppressSync = true;
      const migratedRemote = migrateSnapshot(remoteSnapshot);
      const merged = mergeSnapshots(this.getSnapshot(), migratedRemote);
      this.loadSnapshot(merged);
      await pushSnapshot(this.syncSnapshot, { keepalive: options?.keepalive });
      this.lastSyncedRevision = this.revision;
      this.lastSyncAt = new Date().toISOString();
      if (!this.hydrateErrorPersistent) {
        this.lastSyncError = null;
      }
      this.refreshSnapshot();
      this.emitState();
      this.persist();
    } catch (error) {
      this.handleSyncError(error, 'Failed to sync on load');
    } finally {
      this.suppressSync = false;
    }
  }

  async load(): Promise<void> {
    if (this._messages.length === 0) {
      await this.addMessage(this.entryMessage());
    }
  }

  getEventById(id: string): MessageEvent | undefined {
    return this._messages.find((message) => message.time === id);
  }

  async continue(file: File | UserMessage): Promise<void> {
    if (this.playingMessages) {
      console.log('Skipping continue because there are unplayed messages');
      return;
    }
    let userMessage;
    if (file instanceof UserMessage) {
      userMessage = file;
    } else {
      const language = this.previousEvent?.responseType ?? this.nativeLanguageCode;
      const { message, error } = (await transcribe(file, language));
      if (!message.trim() || this.previousEvent == null) {
        return;
      }
  
      userMessage = new UserMessage(() => this, {
        content: message,
        parentId: this.previousEvent.id,
        error,
      });
      await audio.storeRecording(userMessage.id, file);
    }

    // Add userMessage BEFORE resolving follow-ups so it displays immediately
    await this.addMessage(userMessage);

    if (userMessage.error) {
      return;
    }

    const followUps = this.previousEvent ? (await this.previousEvent.next(userMessage)) ?? [] : [];
    const isLastMessage = this._messages[this._messages.length - 1] === userMessage;
    if (!isLastMessage) {
      return;
    }
    await this.addMessage(...followUps);  
    this.notifyStateChange()
    await this.addMessage(...onComplete(this));
    setTimeout(() => this.notifyStateChange(), 10);
  }


  async retry(message: PromptMessage) {
    const index = this._messages.indexOf(message.event)
    if (message.role === 'user') {
      const removed = this._messages.splice(index);
      const recordingIds = removed
        .filter((event) => event.role === 'user' && event !== message)
        .map((event) => event.id);
      if (recordingIds.length) {
        void audio.purgeRecordings(recordingIds);
      }
      await this.continue(message);
      return;
    }
    this._messages.splice(index + 1);
    this.notifyStateChange();
  }

  async getAttachments(link: Link): Promise<AttachmentItem[]> {
    switch (link.type) {
      case 'failure':
        return [link.reason];
      case 'overview':
        {
          const expression = this.getExpression(link.index);
          const value = expression.value || link.expression;
          return [
            { name: 'Expression', value },
            { name: 'Audio', value: 'Tap refresh to regenerate.', audio: { index: link.index, rate: 1 } },
            { name: 'Pronunciation', value: expression.pronunciation ?? '' },
            { name: 'Translation', value: expression.native ?? '' },
            { name: 'Description', value: expression.breakdown ?? '' },
          ];
        }
      default:
        return [];
    }
  }

  async refreshExpressionDetail(index: number, name: string): Promise<void> {
    const expression = this.getMutableExpression(index);
    if (!expression) {
      return;
    }
    try {
      if (name === 'Pronunciation') {
        expression.pronunciation = await pronounce(expression.value, this.nativeLanguageCode, { retry: true });
      } else if (name === 'Audio') {
        await speak(expression.value, { retry: true });
        return;
      } else if (name === 'Translation') {
        expression.native = await translate(expression.value, this.nativeLanguageCode, { retry: true });
      } else if (name === 'Description') {
        expression.breakdown = await breakdown(expression.value, this.nativeLanguageCode, { retry: true });
      } else {
        return;
      }
      this.notifyStateChange();
    } catch (error) {
      writeError('Failed to refresh expression detail', error);
    }
  }

  async startBreakdown(expressionIndex: number): Promise<void> {
    let parent: MessageEvent | undefined = this.currentChallenge ?? this.previousEvent;
    if (!parent) {
      const entry = this.entryMessage();
      await this.addMessage(entry);
      parent = entry;
    }
    if (!parent) {
      return;
    }
    await this.addMessage(
      new BreakdownMessage(() => this, {
        parentId: parent.id,
        index: expressionIndex,
        role: 'breakdown_prompt',
      })
    );
  }

  async addMessage(...messages: MessageEvent[]): Promise<number> {
    console.log('add messages', messages)
    const length = this._messages.push(...messages);
    this.notifyStateChange();
    this.syncReviewReminders();
    await this.playMessages();
    return length;
  }

  async stopPlayback(): Promise<void> {
    this.applyQueueSilently();
    await audio.stopPlaying();
  }

  private syncReviewReminders(): void {
    const nextReviewAt = this.nextReviewAt;
    const key = `${nextReviewAt ?? 'none'}`;
    if (this.lastReminderKey === key) {
      return;
    }
    this.lastReminderKey = key;
    const allowStateUpdate = this.pushEnabledDevices.length > 0;
    syncReviewReminders(nextReviewAt, { allowStateUpdate }).catch((error) => {
      console.warn('Failed to sync reminders', error);
    });
  }

  setPushEnabledDevice(enabled: boolean, deviceId: string): void {
    const next = new Set(this.pushEnabledDevices);
    if (enabled) {
      next.add(deviceId);
    } else {
      next.delete(deviceId);
    }
    this.pushEnabledDevices = Array.from(next);
    this.notifyStateChange();
  }

  finalizeLesson(lessonId: string): void {
    const lessonEvents = getLessonEvents(this._messages, lessonId);
    const stats = this.expressions.map(() => ({
      successes: 0,
      failures: 0,
      hasRepeatFail: false,
      hasTranslateSuccess: false,
      lastSuccessRole: null as ChallengeSuccessRole | null,
      lastSuccessTime: '',
    }));

    for (const event of lessonEvents) {
      if (event.role === 'repeat_fail') {
        const entry = stats[event.index];
        if (!entry) {
          continue;
        }
        entry.hasRepeatFail = true;
        if (entry.successes === 1) {
          entry.successes = 0;
          entry.failures = 0;
        } else if (entry.successes > 1) {
          entry.failures = Math.min(entry.failures + 1, entry.successes);
        }
        continue;
      }
      if (
        event.role !== 'repeat_success' &&
        event.role !== 'meaning_success' &&
        event.role !== 'translate_success'
      ) {
        continue;
      }
      if (event.role === 'translate_success') {
        const entry = stats[event.index];
        if (!entry) {
          continue;
        }
        entry.hasTranslateSuccess = true;
        entry.successes += 1;
      }
      const entry = stats[event.index];
      if (!entry) {
        continue;
      }
      const currentRole = entry.lastSuccessRole;
      const currentTime = entry.lastSuccessTime;
      const roleWins = !currentRole || SUCCESS_ROLE_RANK[event.role] > SUCCESS_ROLE_RANK[currentRole];
      const timeWins = event.role === currentRole && event.time > currentTime;
      if (roleWins || timeWins) {
        entry.lastSuccessRole = event.role;
        entry.lastSuccessTime = event.time;
      }
    }

    for (let index = 0; index < this.expressions.length; index += 1) {
      const entry = stats[index];
      const role = entry.lastSuccessRole;
      if (!role) {
        continue;
      }
      const expression = this.getMutableExpression(index);
      if (!expression) {
        continue;
      }
      const currentStage = expression.reviewStage ?? 0;
      const currentRole = expression.lastSuccessRole;
      const currentTime = expression.lastSuccessTime ?? '';
      const time = entry.lastSuccessTime;
      const roleWins = !currentRole || SUCCESS_ROLE_RANK[role] > SUCCESS_ROLE_RANK[currentRole];
      const timeWins = role === currentRole && time > currentTime;
      if (roleWins || timeWins) {
        expression.lastSuccessRole = role;
        expression.lastSuccessTime = time;
      }
      if (entry.hasTranslateSuccess && !entry.hasRepeatFail) {
        expression.reviewStage = Math.min(currentStage + 1, REVIEW_INTERVALS_MS.length - 1);
      }
    }
  }

  async goToEntry(): Promise<void> {
    await this.addMessage(this.entryMessage());
  }

  async clearHistory(): Promise<void> {
    this._messages = [];
    this.messageLookup = {};
    this.removedExpressions = [];
    this.expressions = [];
    this.shock = false;
    this.notifyStateChange();
    try {
      localStorage.removeItem(this.key);
    } catch (error) {
      writeError('Failed to clear conversation storage', error);
    }
    await this.load();
  }

  private async playMessages() {
    if (this.playingMessages) {
      return;
    }
    this.playingMessages = true;
    try {
      for (const message of this.unplayedMessages) {
        await playEvent(message, () => this.notifyStateChange());
      }
    } finally {
      this.playingMessages = false;
    }
  }

  private persist(): void {
    if (this.loadFailed) {
      return;
    }
    const save = () => {
      try {
        localStorage.setItem(this.key, JSON.stringify(this.snapshot));
      } catch (error) {
        writeError('Failed to persist conversation', error);
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      (window as any).requestIdleCallback(save);
    } else {
      setTimeout(save, 0);
    }
  }
}

let conversations: Record<string, Conversation> = {};
if (import.meta?.hot) {
  import.meta.hot.dispose(() => {
    conversations = {};
  });
}

export const getConversation = (language: Language, nativeLanguageCode: LanguageCode): Conversation => {
  const key = `${language.code}-${nativeLanguageCode}`;
  return conversations[key] ?? (
    conversations[key] = new Conversation(language, nativeLanguageCode)
  );
}

export default Conversation;
